import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Alert, Chip } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SmsFailedOutlinedIcon from '@mui/icons-material/SmsFailedOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MicIcon from '@mui/icons-material/Mic';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import InboxConversationList from '../components/inbox/InboxConversationList';
import InboxThread from '../components/inbox/InboxThread';
import InboxDetailsPanel from '../components/inbox/InboxDetailsPanel';
import { useAuth } from '../context/AuthContext';
import { getSocket } from '../lib/socket';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { playTicketChime } from '../utils/soundAlert';
import { findNewAssignmentsForAgent } from '../utils/inboxAssignmentNotifications';

const defaultFilters = {
  search: '',
  status: 'all',
  channel: 'all',
  assignedTo: 'all',
  mine: false,
  priority: '', tag: '', slaStatus: '', dateFrom: '', dateTo: '',
};

const INBOX_SECTIONS = [
  { key: 'chats', label: 'Bot Conversations', icon: <ChatBubbleOutlineIcon fontSize="small" /> },
  { key: 'tickets', label: 'Tickets', icon: <ConfirmationNumberOutlinedIcon fontSize="small" /> },
  { key: 'missed-chats', label: 'Missed chats', icon: <SmsFailedOutlinedIcon fontSize="small" /> },
  { key: 'contacts', label: 'Contacts', icon: <ContactsOutlinedIcon fontSize="small" /> },
];

const CHANNEL_META = {
  web: { label: 'Web', icon: <MessageOutlinedIcon fontSize="small" /> },
  whatsapp: { label: 'WhatsApp', icon: <WhatsAppIcon fontSize="small" /> },
  voice: { label: 'Voice', icon: <MicIcon fontSize="small" /> },
};

const channelTone = {
  web: 'bg-sky-500/10 text-sky-700 dark:text-sky-200',
  whatsapp: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-200',
  voice: 'bg-violet-500/10 text-violet-700 dark:text-violet-200',
};

/** Purpose: derive compact initials for a conversation avatar. Why: provides a stable visual identity when contacts have text names but no image. */
const initials = (value = 'V') =>
  String(value || 'V')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
    .slice(0, 2) || 'V';

/** Purpose: match a conversation tag case-insensitively. Why: legacy/imported tags may vary in capitalization. */
const hasTag = (conversation, tag) =>
  Array.isArray(conversation?.tags)
  && conversation.tags.some((item) => String(item || '').trim().toLowerCase() === String(tag || '').trim().toLowerCase());

/** Purpose: normalize the recorded handoff reason. Why: ticket and fallback classification must handle inconsistent case/spacing. */
const getHandoffReason = (conversation) => String(conversation?.metadata?.handoffReason || '').trim().toLowerCase();

/** Purpose: determine if a conversation belongs in the agent-ticket workflow. Why: inbox sections must separate handoffs/support work from bot-only chats. */
const isTicketConversation = (conversation) => {
  if (!conversation) return false;
  const handoffReason = getHandoffReason(conversation);
  const status = String(conversation?.status || '').trim().toLowerCase();
  const hasLiveAgentTag = hasTag(conversation, 'ticket')
    || hasTag(conversation, 'live_chat')
    || hasTag(conversation, 'live_agent')
    || hasTag(conversation, 'support');
  const hasTicketMessage = typeof conversation.lastMessage === 'string' &&
    /assigned to/i.test(conversation.lastMessage);

  return Boolean(
    hasLiveAgentTag
    || ['raise_ticket', 'chat_handoff', 'live_agent', 'live_chat'].includes(handoffReason)
    || conversation.ticketId
    || conversation.metadata?.ticketId
    || conversation.assignedTo
    || conversation.ownerType === 'agent'
    || ['assigned', 'queued', 'pending'].includes(status)
    || conversation.metadata?.handoffRequested
    || conversation.metadata?.handoffQueueActive
    || conversation.metadata?.handoffAssignedAt
    || conversation.metadata?.handoffAssignedMessage
    || conversation.metadata?.systemEvent === 'conversation.assigned'
    || hasTicketMessage
  );
};

/** Purpose: identify tickets that still require work. Why: resolved, closed, and missed records must not inflate the active ticket list/count. */
export const isUnresolvedTicket = (conversation) => isTicketConversation(conversation)
  && !['resolved', 'closed', 'missed'].includes(String(conversation?.status || '').trim().toLowerCase());

/** Purpose: apply ticket status-tab rules to one conversation. Why: sidebar counts and visible ticket rows must agree for active, waiting, missed, and resolved views. */
export const matchesTicketView = (conversation, status = 'all') => {
  if (!isTicketConversation(conversation)) return false;
  const actual = String(conversation?.status || '').trim().toLowerCase();
  if (status === 'resolved') return ['resolved', 'closed'].includes(actual);
  if (status === 'missed') return actual === 'missed' && isTicketConversation(conversation);
  if (!isUnresolvedTicket(conversation)) return false;
  if (status === 'waiting') return ['open', 'queued', 'waiting'].includes(actual);
  return status === 'all' || actual === status;
};

/** Purpose: check whether an async status response belongs to the newest request. Why: stale responses must not undo a more recent resolve/reopen action. */
export const isCurrentStatusEpoch = (requestEpoch, currentEpoch) => requestEpoch === currentEpoch;

/** Purpose: recognize support conversations across tags and handoff metadata. Why: support-specific assignment and display logic must include records saved by older workflows. */
const isSupportConversation = (conversation) =>
  Boolean(conversation?.assignedTo)
  || Boolean(conversation?.metadata?.handoffRequested)
  || ['chat_handoff', 'live_agent'].includes(getHandoffReason(conversation))
  || isTicketConversation(conversation);

/** Purpose: identify conversations that have not entered agent support. Why: bot-conversation views should omit ticket/handoff records. */
const isBotOnlyConversation = (conversation) => !isTicketConversation(conversation);

/** Purpose: normalize API IDs to comparable strings. Why: realtime payloads may use Mongo IDs, string IDs, or objects for the same conversation. */
const normalizeId = (value) => String(value?._id || value || '');

/** Purpose: merge one realtime message without duplicating it. Why: socket events and polling can deliver overlapping updates to the thread. */
const mergeRealtimeMessage = (currentMessages, incomingMessage) => {
  if (!incomingMessage) return currentMessages;

  const incomingId = normalizeId(incomingMessage._id);
  if (incomingId && currentMessages.some((message) => normalizeId(message?._id) === incomingId)) {
    return currentMessages;
  }

  return [...currentMessages, incomingMessage].sort((left, right) => {
    const leftTime = new Date(left?.timestamp || left?.createdAt || 0).getTime();
    const rightTime = new Date(right?.timestamp || right?.createdAt || 0).getTime();
    return leftTime - rightTime;
  });
};

/** Purpose: coordinate the inbox's conversation, ticket, missed-chat, and contact views. Why: a single controller keeps live updates, filters, and thread selection consistent across categories. */
export default function Inbox() {
  const navigate = useNavigate();
  const location = useLocation();
  const { section } = useParams();
  const { selectedBot, selectedEnv, isAdmin, user, agentPresence, updateAgentPresence } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);

  /** Purpose: Read the conversation ID from the current URL. Why: Deep links should open the intended inbox conversation. */
  const requestedConversationId = useMemo(
    () => new URLSearchParams(location.search).get('conversationId') || '',
    [location.search],
  );

  const [activeSection, setActiveSection] = useState(
    ['dashboard', 'chats', 'tickets', 'missed-chats', 'contacts'].includes(section)
      ? section
      : (requestedConversationId ? 'chats' : 'dashboard'),
  );

  useEffect(() => {
    if (section && ['dashboard', 'chats', 'tickets', 'missed-chats', 'contacts'].includes(section)) {
      setActiveSection(section);
      setFilters((current) => ({ ...current, status: section === 'missed-chats' ? 'missed' : 'all' }));
      if (!requestedConversationId) {
        currentConversationIdRef.current = null;
        setSelectedConversation(null);
        setMessages([]);
      }
    }
  }, [requestedConversationId, section]);
  const [chatView, setChatView] = useState('all');
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [agents, setAgents] = useState([]);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, limit: 25, total: 0 });
  const [missedCount, setMissedCount] = useState(0);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [resolvingConversationId, setResolvingConversationId] = useState('');
  const resolvingConversationIdRef = useRef('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [socketReady, setSocketReady] = useState(false);
  const [remoteTyping, setRemoteTyping] = useState(false);
  const [remoteTypingText, setRemoteTypingText] = useState('');
  const [capabilities, setCapabilities] = useState({ text: true, file: true, typing: true });
  const [notifications, setNotifications] = useState([]);
  const [notificationPrefs, setNotificationPrefs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('agent_notification_preferences')) || { browser: false, sound: true };
    } catch {
      return { browser: false, sound: true };
    }
  });
  const [contacts, setContacts] = useState([]);
  const [analyticsRange, setAnalyticsRange] = useState('7');
  const [analytics, setAnalytics] = useState(null);
  const [customPresenceText, setCustomPresenceText] = useState(agentPresence?.customStatus || '');
  const [showDetails, setShowDetails] = useState(true);
  const [headerNavigationTarget, setHeaderNavigationTarget] = useState(null);
  const [headerActionsTarget, setHeaderActionsTarget] = useState(() => (typeof document !== 'undefined' ? document.getElementById('inbox-header-actions') : null));
  const [soundTested, setSoundTested] = useState('');
  const currentConversationIdRef = useRef(null);
  // Prevent in-flight list/message reads from overwriting a newer lifecycle
  // mutation (e.g. Resolve) when a polling response arrives out of order.
  const conversationStatusEpochRef = useRef(0);
  // Socket events have no request epoch, so retain terminal status as an
  // authority fence until an explicit reopen or handoff arrives.
  const resolvedConversationIdsRef = useRef(new Map());
  const prevConversationsMapRef = useRef(new Map());
  const initialLoadDoneRef = useRef(false);
  const pollCountRef = useRef(0);

  useEffect(() => {
    setHeaderNavigationTarget(document.getElementById('inbox-header-navigation'));
    setHeaderActionsTarget(document.getElementById('inbox-header-actions'));
  }, []);

  const selectedConversationId = activeSection === 'contacts' ? null : selectedConversation?._id || null;
  /** Purpose: Update the current agent's presence and reconcile the agent list. Why: Assignment availability must reflect the agent's latest saved status. */
  const changeMyPresence = useCallback(async (status, customStatus = '') => {
    try {
      const updated = await updateAgentPresence?.({ status, customStatus });
      if (updated) {
        setAgents((current) => [updated, ...current.filter((agent) => String(agent.userId || agent._id) !== String(updated.userId || updated._id))]);
      }
    } catch (presenceError) {
      setError(presenceError.response?.data?.error || 'Could not update agent status.');
    }
  }, [updateAgentPresence]);

  /** Purpose: Persist notification preferences and request browser permission when needed. Why: Alerts should follow explicit agent preferences and the browser's permission state. */
  const updateNotificationPrefs = useCallback(async (patch) => {
    const next = { ...notificationPrefs, ...patch };
    if (next.browser && typeof window !== 'undefined' && window.Notification) {
      if (window.Notification.permission === 'default') {
        const perm = await window.Notification.requestPermission();
        next.browser = perm === 'granted';
      } else if (window.Notification.permission === 'denied') {
        next.browser = false;
        alert('Browser notifications are blocked in your browser settings. Please allow notifications for this site.');
      }
    }
    setNotificationPrefs(next);
    localStorage.setItem('agent_notification_preferences', JSON.stringify(next));
    window.dispatchEvent(new Event('agent_notification_preferences_changed'));
  }, [notificationPrefs]);

  useEffect(() => {
    if (!selectedConversation?.channel) return;
    axios.get(`/api/v1/inbox/capabilities/${selectedConversation.channel}`).then((response) => setCapabilities(response.data.capabilities || {})).catch(() => setCapabilities({ text: true, file: false, typing: false }));
  }, [selectedConversation?.channel]);

  useEffect(() => {
    const nextSection = ['dashboard', 'chats', 'tickets', 'missed-chats', 'contacts'].includes(section) ? section : 'dashboard';
    setActiveSection(nextSection);
    setFilters((current) => ({ ...current, status: nextSection === 'missed-chats' ? 'missed' : 'all' }));
  }, [section]);

  /** Purpose: Load agents available to the selected bot. Why: Assignment controls must not expose agents outside the active bot. */
  const fetchAgents = useCallback(async () => {
    if (!selectedBot) {
      setAgents([]);
      return;
    }
    try {
      const response = await axios.get('/api/v1/agents', { params: { botId: selectedBot } });
      setAgents(response.data.agents || []);
    } catch (agentError) {
      setAgents([]);
    }
  }, [selectedBot]);

  /** Purpose: Load canned replies for the selected bot and environment. Why: Agents need environment-scoped response templates. */
  const fetchCannedResponses = useCallback(async () => {
    if (!selectedBot) return setCannedResponses([]);
    try {
      const response = await axios.get('/api/v1/agent-desk/canned-responses', { params: { botId: selectedBot, env: selectedEnv || 'dev' } });
      setCannedResponses(response.data.cannedResponses || []);
    } catch (error) { setCannedResponses([]); }
  }, [selectedBot, selectedEnv]);
  /** Purpose: Search contacts for the active bot and environment. Why: Contact context should match the current inbox scope. */
  const fetchContacts = useCallback(async () => {
    if (!selectedBot) return setContacts([]);
    try { const response = await axios.get('/api/v1/agent-desk/contacts', { params: { botId: selectedBot, env: selectedEnv, search: filters.search, limit: 100 } }); setContacts(response.data.contacts || []); } catch (error) { setContacts([]); }
  }, [filters.search, selectedBot, selectedEnv]);
  /** Purpose: Load inbox metrics for the selected time range. Why: Dashboard summaries must use the same bot/environment as the conversation queue. */
  const fetchAnalytics = useCallback(async () => {
    if (!selectedBot) return setAnalytics(null);
    const to = new Date(); const from = new Date(to.getTime() - (Number(analyticsRange) * 86400000));
    try { const response = await axios.get('/api/v1/agent-desk/analytics', { params: { botId: selectedBot, env: selectedEnv, from: from.toISOString(), to: to.toISOString() } }); setAnalytics(response.data); } catch { setAnalytics(null); }
  }, [analyticsRange, selectedBot, selectedEnv]);

  /** Purpose: Load or append a page of filtered conversations while guarding against stale status responses. Why: Polling and user filter changes must not overwrite newer inbox state. */
  const fetchConversations = useCallback(async (nextPage = 1, append = false, silent = false) => {
    if (!selectedBot) {
      setConversations([]);
      setPagination({ page: 1, pages: 1, limit: 25, total: 0 });
      return;
    }

    const statusEpoch = conversationStatusEpochRef.current;
    if (!silent) {
      setLoadingConversations(true);
      setError('');
    }
    try {
      const response = await axios.get('/api/v1/inbox', {
        params: {
          botId: selectedBot,
          env: selectedEnv || 'dev',
          page: nextPage,
          limit: pagination.limit || 25,
          search: filters.search || '',
          status: filters.status === 'all' ? '' : filters.status,
          channel: filters.channel === 'all' ? '' : filters.channel,
          assignedTo: filters.assignedTo === 'all' ? '' : filters.assignedTo,
          mine: filters.mine ? 'true' : 'false',
          priority: filters.priority, tag: filters.tag, slaStatus: filters.slaStatus, dateFrom: filters.dateFrom, dateTo: filters.dateTo,
        },
      });

      // Ignore a snapshot requested before/during a status mutation. The
      // resolve response is authoritative and a delayed poll must not restore
      // the previous OPEN badge/actions over it.
      if (!isCurrentStatusEpoch(statusEpoch, conversationStatusEpochRef.current)) return;

      const items = response.data.conversations || [];
      if (!initialLoadDoneRef.current) {
        items.forEach((item) => {
          const id = normalizeId(item?._id);
          if (id) prevConversationsMapRef.current.set(id, item);
        });
        initialLoadDoneRef.current = true;
      }
      items.forEach((item) => {
        const id = normalizeId(item?._id);
        if (!id) return;
        if (['resolved', 'closed'].includes(String(item.status || '').toLowerCase())) {
          resolvedConversationIdsRef.current.set(id, Date.parse(item.resolvedAt || item.updatedAt || '') || Date.now());
        }
      });
      setConversations((current) => (append ? [...current, ...items] : items));
      setPagination(response.data.pagination || { page: 1, pages: 1, limit: 25, total: items.length });
      if (Number.isFinite(Number(response.data.stats?.missed))) setMissedCount(Number(response.data.stats.missed));

      const currentSelectedId = currentConversationIdRef.current;

      if (!currentSelectedId && items[0]) {
        setSelectedConversation(items[0]);
      } else if (currentSelectedId) {
        /** Purpose: Find the selected conversation in the refreshed result. Why: The UI should retain selection when polling replaces list objects. */
        const refreshed = items.find((item) => normalizeId(item._id) === normalizeId(currentSelectedId));
        if (refreshed) {
          setSelectedConversation(refreshed);
        }
      }
    } catch (listError) {
      setError(listError.response?.data?.error || 'Failed to load inbox conversations');
    } finally {
      if (!silent) setLoadingConversations(false);
    }
  }, [filters.assignedTo, filters.channel, filters.mine, filters.search, filters.status, filters.priority, filters.tag, filters.slaStatus, filters.dateFrom, filters.dateTo, pagination.limit, selectedBot, selectedEnv]);

  /** Purpose: Load messages for a conversation while preserving current status when requested. Why: Conversation changes and polling must not apply stale message data. */
  const fetchMessages = useCallback(async (conversationId, { silent = false, preserveConversationStatus = false } = {}) => {
    if (!conversationId) {
      setMessages([]);
      return;
    }

    const requestedId = normalizeId(conversationId);
    const statusEpoch = conversationStatusEpochRef.current;
    if (!silent) {
      setMessages([]);
      setLoadingMessages(true);
    }
    try {
      const response = await axios.get(`/api/v1/inbox/conversations/${conversationId}/messages`, {
        params: { limit: 150, page: 1 },
      });
      if (!isCurrentStatusEpoch(statusEpoch, conversationStatusEpochRef.current)) return;
      if (normalizeId(currentConversationIdRef.current) !== requestedId) return;
      setMessages(response.data.messages || []);
      const conversation = response.data.conversation;
      if (!preserveConversationStatus && conversation && normalizeId(conversation._id) === requestedId) {
        if (['resolved', 'closed'].includes(String(conversation.status || '').toLowerCase())) {
          resolvedConversationIdsRef.current.set(requestedId, Date.parse(conversation.resolvedAt || conversation.updatedAt || '') || Date.now());
        }
        setSelectedConversation(conversation);
        setConversations((current) => {
          const convId = normalizeId(conversation._id);
          if (!convId) return current;
          /** Purpose: Check whether a conversation is already in the current list. Why: Realtime and polling updates should avoid duplicate rows. */
          const exists = current.some((c) => normalizeId(c._id) === convId);
          if (exists) {
            return current.map((c) => (normalizeId(c._id) === convId ? { ...c, ...conversation } : c));
          }
          return [conversation, ...current];
        });
      }
      if (!silent) await axios.patch(`/api/v1/inbox/conversations/${conversationId}/read`);
    } catch (messageError) {
      if (normalizeId(currentConversationIdRef.current) === requestedId) {
        setError(messageError.response?.data?.error || 'Failed to load messages');
      }
    } finally {
      if (!silent && normalizeId(currentConversationIdRef.current) === requestedId) {
        setLoadingMessages(false);
      }
    }
  }, []);

  // Realtime remains the primary path. This silent reconciliation guarantees
  // that a missed socket event never forces the agent to refresh the browser,
  // AND detects newly arrived tickets/conversations to trigger alerts reliably.
  useEffect(() => {
    if (!selectedBot) return undefined;
    /** Purpose: Refresh conversation data and agent presence on the polling interval. Why: The inbox stays current when realtime delivery is unavailable or delayed. */
    const timer = window.setInterval(async () => {
      const statusEpoch = conversationStatusEpochRef.current;
      try {
        pollCountRef.current += 1;
        if (pollCountRef.current % 5 === 0) {
          void fetchAgents();
        }
        const response = await axios.get('/api/v1/inbox', {
          params: {
            botId: selectedBot,
            env: selectedEnv || 'dev',
            page: 1,
            limit: pagination.limit || 25,
            search: filters.search || '',
            status: filters.status === 'all' ? '' : filters.status,
            channel: filters.channel === 'all' ? '' : filters.channel,
            assignedTo: filters.assignedTo === 'all' ? '' : filters.assignedTo,
            mine: filters.mine ? 'true' : 'false',
            priority: filters.priority,
            tag: filters.tag,
            slaStatus: filters.slaStatus,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
          },
        });
        if (!isCurrentStatusEpoch(statusEpoch, conversationStatusEpochRef.current)) return;
        const items = response.data.conversations || [];
        const currentUserIds = [user?._id, user?.id, user?.userId].filter(Boolean).map(String);
        /** Purpose: Resolve the signed-in user's matching agent record. Why: Presence and assignment UI need the active agent identity. */
        const currentAgent = agents.find((agent) => currentUserIds.includes(String(agent.userId || agent._id)));
        const currentAgentIds = [...currentUserIds, currentAgent?._id].filter(Boolean);
        const shouldAlertFromPolling = initialLoadDoneRef.current
          && !socketReady
          && (agentPresence?.status || 'online') === 'online';
        const newAssignments = shouldAlertFromPolling
          ? findNewAssignmentsForAgent({
            conversations: items,
            previousConversations: prevConversationsMapRef.current,
            agentIds: currentAgentIds,
          })
          : [];

        for (const item of items) {
          const id = normalizeId(item._id);
          if (id) prevConversationsMapRef.current.set(id, item);
        }
        initialLoadDoneRef.current = true;
        if (items.length > 0) setConversations(items);

        for (const conversation of newAssignments) {
          window.dispatchEvent(new CustomEvent('inbox:ticket-assignment-polled', {
            detail: { conversation },
          }));
        }
        const activeId = currentConversationIdRef.current;
        if (activeId) void fetchMessages(activeId, { silent: true });
      } catch (err) {
        // Silent reconciliation error
      }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [agentPresence?.status, agents, fetchAgents, fetchMessages, filters, pagination.limit, selectedBot, selectedEnv, socketReady, user?._id, user?.id, user?.userId]);

  useEffect(() => {
    if (!requestedConversationId || !selectedBot) return;
    currentConversationIdRef.current = requestedConversationId;
    if (activeSection === 'dashboard' || activeSection === 'contacts') {
      setActiveSection(section === 'tickets' ? 'tickets' : 'chats');
    }
    void fetchMessages(requestedConversationId);
  }, [activeSection, fetchMessages, requestedConversationId, section, selectedBot]);

  useEffect(() => {
    fetchAgents();
    fetchCannedResponses();
    fetchContacts();
    fetchAnalytics();
  }, [fetchAgents, fetchCannedResponses, fetchContacts, fetchAnalytics]);

  useEffect(() => {
    if (!selectedBot) return;
    const currentStatus = agentPresence?.status || 'online';
    try {
      const p = axios.post('/api/v1/agents/presence', {
        botId: selectedBot,
        status: currentStatus,
        customStatus: agentPresence?.customStatus || '',
        isAvailable: currentStatus === 'online',
      });
      if (p && typeof p.catch === 'function') {
        p.catch(() => {});
      }
    } catch {}
  }, [agentPresence?.customStatus, agentPresence?.status, selectedBot]);

  useEffect(() => {
    fetchConversations(1, false);
    setSelectedConversation(null);
    setMessages([]);
  }, [fetchConversations, selectedBot, selectedEnv]);

  useEffect(() => {
    const socket = getSocket();
    if (!selectedBot) {
      return undefined;
    }

    const roomEnv = selectedEnv || 'dev';
    const agentId = user?._id || user?.id || user?.userId;
    // Purpose: join socket rooms for the active bot/environment. Why: only relevant workspace events should update this Inbox instance.
    const joinWorkspaceRooms = () => {
      socket.emit('inbox:join', { botId: selectedBot, envName: roomEnv }, (response) => {
        setSocketReady(Boolean(response?.success));
        if (!response?.success) console.error('[Inbox] inbox:join failed', response);
      });

      if (agentId) {
        socket.emit(
          'agent:join',
          { agentId, botId: selectedBot, envName: roomEnv },
          (response) => {
            if (!response?.success) console.error('[Inbox] agent:join failed', response);
          },
        );
      }
    };
    // Purpose: mark live updates unavailable after socket loss. Why: the header can accurately show that polling is currently the fallback.
    const onDisconnect = () => setSocketReady(false);

    socket.on('connect', joinWorkspaceRooms);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) joinWorkspaceRooms();

    // Purpose: merge conversation create/update events into Inbox state. Why: newly created and changed records should appear without a full page reload.
    const onUpsert = (payload = {}) => {
      const incoming = payload.conversation;
      if (!incoming) return;
      if (selectedBot && incoming.botId && normalizeId(incoming.botId) !== normalizeId(selectedBot)) return;

      const incomingId = normalizeId(incoming._id);
      const isResolved = ['resolved', 'closed'].includes(String(incoming.status || '').toLowerCase());
      const hasExplicitReopenSignal = Boolean(
        incoming.metadata?.handoffRequested
        || incoming.metadata?.handoffQueueActive
        || incoming.metadata?.agentTakeover
      );
      if (isResolved) {
        resolvedConversationIdsRef.current.set(incomingId, Date.parse(incoming.resolvedAt || incoming.updatedAt || '') || Date.now());
      } else if (resolvedConversationIdsRef.current.has(incomingId)) {
        const resolvedAt = resolvedConversationIdsRef.current.get(incomingId);
        const incomingAt = Date.parse(incoming.updatedAt || incoming.createdAt || '');
        if (!hasExplicitReopenSignal || !incomingAt || incomingAt <= resolvedAt) return;
        resolvedConversationIdsRef.current.delete(incomingId);
      }
      prevConversationsMapRef.current.set(incomingId, incoming);

      setConversations((current) => {
        /** Purpose: Remove an incoming conversation ID from duplicate candidates. Why: Realtime reconciliation must keep one row per conversation. */
        const next = current.filter((item) => normalizeId(item?._id) !== incomingId);
        return [incoming, ...next].sort((a, b) => new Date(b.lastMessageAt || b.updatedAt || 0) - new Date(a.lastMessageAt || a.updatedAt || 0));
      });

      if (normalizeId(currentConversationIdRef.current) === incomingId) {
        setSelectedConversation(incoming);
      }
    };

    // Purpose: handle server-confirmed assignment changes. Why: assignment badges, alerts, and selected thread data must update from the canonical event.
    const onAssignConversation = (payload = {}) => {
      const incoming = payload.conversation;
      if (!incoming) return;
      if (selectedBot && incoming.botId && normalizeId(incoming.botId) !== normalizeId(selectedBot)) return;

      onUpsert({ conversation: incoming });
      if (notificationPrefs.sound) {
        playTicketChime();
      }
    };

    // Purpose: append a message event to its conversation and update previews/unread state. Why: agent replies and customer messages must arrive live in the open Inbox.
    const onMessage = (payload = {}) => {
      const incomingConversationId = payload.conversationId || payload.message?.conversationId;
      if (!incomingConversationId) return;

      if (normalizeId(currentConversationIdRef.current) === normalizeId(incomingConversationId)) {
        if (payload.message) {
          setMessages((current) => mergeRealtimeMessage(current, payload.message));
        } else {
          // Compatibility fallback for older emitters which only send a conversation id.
          void fetchMessages(normalizeId(incomingConversationId));
        }
      }

      // The paired inbox:conversation-upsert event updates the list without another
      // request. Avoiding a full list reload here also prevents it from replacing the
      // currently selected conversation while rapid user/bot events are arriving.
    };

    // Purpose: refresh live agent availability/capacity after presence events. Why: the available-agent count and assignment UI depend on current roster state.
    const onAgentUpdate = (payload = {}) => {
      const incomingAgent = payload.agent;
      if (!incomingAgent) return;

      setAgents((current) => {
        /** Purpose: Replace a stale agent record with its incoming update. Why: Presence refreshes should update rather than duplicate agent entries. */
        const next = current.filter((agent) => String(agent.userId || agent._id) !== String(incomingAgent.userId || incomingAgent._id));
        return [incomingAgent, ...next].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      });
    };
    // Purpose: surface workspace notifications in the Inbox. Why: users need assignment/system alerts without leaving their current conversation.
    const onNotification = (payload = {}) => {
      const notification = payload.notification;
      if (!notification) return;
      setNotifications((current) => [notification, ...current].slice(0, 10));
    };

    socket.on('inbox:conversation-upsert', onUpsert);
    socket.on('assign_conversation', onAssignConversation);
    socket.on('inbox:message-created', onMessage);
    socket.on('agent_online', onAgentUpdate);
    socket.on('agent_offline', onAgentUpdate);
    socket.on('agent_busy', onAgentUpdate);
    socket.on('agent_updated', onAgentUpdate);
    socket.on('notification.created', onNotification);

    return () => {
      socket.off('inbox:conversation-upsert', onUpsert);
      socket.off('assign_conversation', onAssignConversation);
      socket.off('inbox:message-created', onMessage);
      socket.off('agent_online', onAgentUpdate);
      socket.off('agent_offline', onAgentUpdate);
      socket.off('agent_busy', onAgentUpdate);
      socket.off('agent_updated', onAgentUpdate);
      socket.off('notification.created', onNotification);
      socket.off('connect', joinWorkspaceRooms);
      socket.off('disconnect', onDisconnect);
      socket.emit('inbox:leave', { botId: selectedBot, envName: roomEnv });
      if (agentId) socket.emit('agent:leave', { agentId });
      setSocketReady(false);
    };
  }, [fetchConversations, fetchMessages, notificationPrefs.browser, notificationPrefs.sound, selectedBot, selectedEnv, user?._id, user?.id, user?.userId]);

  useEffect(() => {
    const socket = getSocket();
    if (!selectedConversationId) return undefined;

    // Purpose: subscribe to the selected conversation's private event room. Why: typing and message events can be scoped to the thread currently being viewed.
    const joinConversationRoom = () => {
      socket.emit('conversation:join', { conversationId: selectedConversationId }, (response) => {
        if (!response?.success) console.error('[Inbox] conversation:join failed', response);
      });
    };

    socket.on('connect', joinConversationRoom);
    if (socket.connected) joinConversationRoom();
    currentConversationIdRef.current = selectedConversationId;
    fetchMessages(selectedConversationId);
    // Purpose: update the thread's typing indicator from socket events. Why: agents need immediate context when another participant is composing a message.
    const onTyping = (payload) => {
      if (normalizeId(payload.conversationId) === normalizeId(selectedConversationId)) {
        setRemoteTyping(Boolean(payload.typing));
        setRemoteTypingText(payload.text || '');
      }
    };
    socket.on('conversation:typing', onTyping);

    return () => {
      socket.emit('conversation:leave', { conversationId: selectedConversationId });
      socket.off('connect', joinConversationRoom);
      socket.off('conversation:typing', onTyping);
      setRemoteTyping(false);
      setRemoteTypingText('');
    };
  }, [fetchMessages, selectedConversationId]);
  /** Purpose: Send typing state for the selected conversation when supported. Why: Typing indicators should respect the channel's declared capabilities. */
  const emitTyping = useCallback((typing) => { if (selectedConversationId && capabilities.typing) getSocket().emit('conversation:typing', { conversationId: selectedConversationId, typing }); }, [capabilities.typing, selectedConversationId]);

  /** Purpose: Merge a filter edit into current inbox filters. Why: Updating one criterion should preserve the other active filters. */
  const handleFilterChange = useCallback((patch) => {
    setFilters((current) => ({ ...current, ...patch }));
  }, []);

  /** Purpose: Fetch the next conversation page when one exists. Why: Pagination should not request beyond the available result set. */
  const handleLoadMore = useCallback(() => {
    if (pagination.page < pagination.pages) {
      fetchConversations((pagination.page || 1) + 1, true);
    }
  }, [fetchConversations, pagination.page, pagination.pages]);

  /** Purpose: Select a conversation, synchronize the URL, and load its context. Why: The message thread and side panel must refer to the same conversation. */
  const handleSelectConversation = useCallback((conversation) => {
    if (!conversation) return;
    const convId = normalizeId(conversation._id);
    setSelectedConversation((current) => (
      normalizeId(current?._id) === convId && current === conversation ? current : conversation
    ));
    currentConversationIdRef.current = convId || null;
    setConversations((current) => {
      if (!convId) return current;
      /** Purpose: Find the selected conversation before replacing it in the list. Why: Selection reconciliation should preserve current data if the refreshed row is absent. */
      const existing = current.find((item) => normalizeId(item._id) === convId);
      if (existing === conversation) return current;
      if (existing) return current.map((item) => (normalizeId(item._id) === convId ? conversation : item));
      return [conversation, ...current];
    });
  }, []);

  // Handle external selection (from global notification banner click or custom event)
  useEffect(() => {
    // Purpose: select a conversation requested by an external inbox notification. Why: notification actions can open the correct thread without duplicating selection logic.
    const handleSelectEvent = (event) => {
      const conv = event.detail?.conversation;
      const convId = normalizeId(event.detail?.conversationId || conv?._id);
      if (!convId && !conv) return;

      const targetSection = event.detail?.section || (isTicketConversation(conv) ? 'tickets' : 'chats');
      setActiveSection(targetSection);
      setError('');

      if (conv) {
        handleSelectConversation(conv);
      }
      if (convId) {
        currentConversationIdRef.current = convId;
        void fetchMessages(convId);
      }
    };
    window.addEventListener('select_inbox_conversation', handleSelectEvent);

    // Also check sessionStorage for pending conversation transition from another page
    try {
      const stored = sessionStorage.getItem('pending_inbox_conversation');
      if (stored) {
        sessionStorage.removeItem('pending_inbox_conversation');
        const parsed = JSON.parse(stored);
        if (parsed?.conversation || parsed?.conversationId) {
          const conv = parsed.conversation;
          const convId = normalizeId(parsed.conversationId || conv?._id);
          const targetSection = parsed.section || (isTicketConversation(conv) ? 'tickets' : 'chats');
          setActiveSection(targetSection);
          if (conv) handleSelectConversation(conv);
          if (convId) {
            currentConversationIdRef.current = convId;
            void fetchMessages(convId);
          }
        }
      }
    } catch {}

    return () => window.removeEventListener('select_inbox_conversation', handleSelectEvent);
  }, [fetchMessages, handleSelectConversation]);

  useEffect(() => {
    if (!requestedConversationId || conversations.length === 0) return;
    /** Purpose: Resolve the conversation requested by the current deep link. Why: URL navigation should select the corresponding list item when available. */
    const match = conversations.find((item) => normalizeId(item._id) === normalizeId(requestedConversationId));
    if (match) {
      handleSelectConversation(match);
      if (activeSection === 'dashboard' || activeSection === 'contacts') {
        setActiveSection(isTicketConversation(match) ? 'tickets' : 'chats');
      }
    }
  }, [activeSection, conversations, handleSelectConversation, requestedConversationId]);

  /** Purpose: Persist a conversation patch and reconcile selected and list state. Why: Agent actions need immediate consistent inbox data after server confirmation. */
  const updateConversation = useCallback(async (conversationId, payload) => {
    const response = await axios.patch(`/api/v1/inbox/conversations/${conversationId}`, payload);
    const updated = response.data.conversation;
    setSelectedConversation(updated);
    setConversations((current) => current.map((item) => (item._id === updated._id ? updated : item)));
    return updated;
  }, []);

  /** Purpose: Send a nonempty reply and refresh the conversation thread. Why: Agent responses must become visible with the latest server message state. */
  const sendMessage = useCallback(async (text) => {
    if (!selectedConversationId || !text.trim()) return;
    setSending(true);
    try {
      await axios.post(`/api/v1/inbox/conversations/${selectedConversationId}/messages`, {
        message: text,
        type: 'text',
      });
      await fetchMessages(selectedConversationId);
      await fetchConversations(1, false);
    } catch (sendError) {
      setError(sendError.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  }, [fetchConversations, fetchMessages, selectedConversationId]);

  /** Purpose: Upload a file to the selected conversation and refresh its thread. Why: Attachments need the same conversation scope and timeline as text messages. */
  const sendAttachment = useCallback(async (file) => {
    if (!selectedConversationId || !file) return;
    const body = new FormData(); body.append('attachment', file);
    await axios.post(`/api/v1/inbox/conversations/${selectedConversationId}/attachments`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
    await fetchMessages(selectedConversationId);
  }, [fetchMessages, selectedConversationId]);
  /** Purpose: Fetch and open an attachment using its conversation and attachment IDs. Why: Private files should be retrieved through the authorized inbox endpoint. */
  const openAttachment = useCallback(async (attachment) => {
    if (!selectedConversationId) return;
    const response = await axios.get(`/api/v1/inbox/conversations/${selectedConversationId}/attachments/${attachment.attachmentId}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = attachment.name || 'attachment'; link.click(); window.URL.revokeObjectURL(url);
  }, [selectedConversationId]);

  /** Purpose: Accept the selected conversation and refresh the queue. Why: Queue ownership changes should be reflected from server state. */
  const acceptConversation = useCallback(async () => { if (selectedConversationId) { await axios.post(`/api/v1/inbox/conversations/${selectedConversationId}/accept`); await fetchConversations(); } }, [fetchConversations, selectedConversationId]);
  /** Purpose: Submit an agent's rejection reason and refresh the queue. Why: Queue decisions need an auditable explanation. */
  const rejectConversation = useCallback(async () => { const reason = window.prompt('Rejection reason'); if (selectedConversationId && reason) { await axios.post(`/api/v1/inbox/conversations/${selectedConversationId}/reject`, { reason }); await fetchConversations(); } }, [fetchConversations, selectedConversationId]);

  /** Purpose: Persist a private note and refresh conversation context. Why: Internal handoff information must appear in the thread without sending to the customer. */
  const addNote = useCallback(async (text) => {
    if (!selectedConversationId) return;
    await axios.post(`/api/v1/inbox/conversations/${selectedConversationId}/notes`, { text });
    await fetchConversations(1, false);
    await fetchMessages(selectedConversationId);
  }, [fetchConversations, fetchMessages, selectedConversationId]);

  /** Purpose: Add a tag to the selected conversation and refresh its metadata. Why: Tags drive inbox organization and filtering. */
  const addTag = useCallback(async (tag) => {
    if (!selectedConversationId) return;
    await axios.post(`/api/v1/inbox/conversations/${selectedConversationId}/tags`, { tag });
    await fetchConversations(1, false);
    await fetchMessages(selectedConversationId);
  }, [fetchConversations, fetchMessages, selectedConversationId]);

  /** Purpose: Remove a tag and refresh conversation metadata. Why: The UI must reflect the server's final tag state. */
  const removeTag = useCallback(async (tag) => {
    if (!selectedConversationId) return;
    await axios.delete(`/api/v1/inbox/conversations/${selectedConversationId}/tags/${encodeURIComponent(tag)}`);
    await fetchConversations(1, false);
    await fetchMessages(selectedConversationId);
  }, [fetchConversations, fetchMessages, selectedConversationId]);

  /** Purpose: Assign or unassign the selected conversation. Why: Ownership updates should pass through the shared conversation update path. */
  const assignConversation = useCallback(async (assignedTo) => {
    if (!selectedConversationId) return;
    await updateConversation(selectedConversationId, { assignedTo: assignedTo || null });
  }, [selectedConversationId, updateConversation]);

  /** Purpose: Change the selected conversation status with duplicate-resolution guards. Why: Repeated resolve requests should not race or re-close an already resolved chat. */
  const setStatus = useCallback(async (status) => {
    if (!selectedConversationId) return;
    const targetConversationId = normalizeId(selectedConversationId);
    if (status === 'resolved' && (
      resolvingConversationIdRef.current === targetConversationId
      || ['resolved', 'closed'].includes(String(selectedConversation?.status || '').toLowerCase())
    )) return;
    if (status === 'resolved') {
      // A ref closes the tiny window before React commits the disabled button,
      // so rapid double-clicks can never dispatch duplicate resolve requests.
      resolvingConversationIdRef.current = targetConversationId;
      setResolvingConversationId(targetConversationId);
    }
    // Fence reads started before or while this mutation is pending. They may
    // complete after the Resolve response and otherwise restore stale status.
    conversationStatusEpochRef.current += 1;
    try {
      let response;
      if (status === 'resolved') {
        response = await axios.post(`/api/v1/inbox/conversations/${targetConversationId}/resolve`, {});
      } else if (status === 'open' && selectedConversation?.status === 'resolved') {
        response = await axios.post(`/api/v1/inbox/conversations/${targetConversationId}/reopen`, {});
      } else {
        response = { data: { conversation: await updateConversation(targetConversationId, { status }) } };
      }

      // Apply the server's authoritative status immediately. This keeps the
      // selected thread in sync even if its list refresh is delayed or fails.
      const updated = response?.data?.conversation;
      if (updated) {
        const updatedId = normalizeId(updated._id);
        if (['resolved', 'closed'].includes(String(updated.status || '').toLowerCase())) {
          resolvedConversationIdsRef.current.set(updatedId, Date.parse(updated.resolvedAt || updated.updatedAt || '') || Date.now());
        } else {
          resolvedConversationIdsRef.current.delete(updatedId);
        }
      }
      // Invalidate reads that began while the mutation was in flight too.
      conversationStatusEpochRef.current += 1;
      if (updated) {
        setSelectedConversation(updated);
        setConversations((current) => current.map((item) => (
          normalizeId(item._id) === normalizeId(updated._id) ? { ...item, ...updated } : item
        )));
      }

      await fetchConversations(1, false);
      // Resolving can append a bot close message. Refresh the transcript but
      // do not let its conversation snapshot replace the status returned by
      // the resolve endpoint (a stale read here made agents click Resolve again).
      await fetchMessages(targetConversationId, { preserveConversationStatus: true });
      if (updated) {
        // Reject any polling/list request that started during the refreshes
        // before restoring the mutation response as the final UI state.
        conversationStatusEpochRef.current += 1;
        setSelectedConversation((current) => (
          normalizeId(current?._id) === normalizeId(updated._id) ? { ...current, ...updated } : current
        ));
        setConversations((current) => current.map((item) => (
          normalizeId(item?._id) === normalizeId(updated._id) ? { ...item, ...updated } : item
        )));
      }
    } catch (statusError) {
      const detail = statusError.response?.data?.message || statusError.response?.data?.error;
      setError(detail || `Could not ${status === 'resolved' ? 'resolve' : 'update'} this conversation. Please retry.`);
    } finally {
      if (status === 'resolved') {
        resolvingConversationIdRef.current = resolvingConversationIdRef.current === targetConversationId
          ? ''
          : resolvingConversationIdRef.current;
        setResolvingConversationId((current) => current === targetConversationId ? '' : current);
      }
    }
  }, [fetchConversations, fetchMessages, selectedConversation?.status, selectedConversationId, updateConversation]);

  /** Purpose: Merge the signed-in agent into the server presence list when absent. Why: The current operator must remain represented even during delayed presence propagation. */
  const reconciledAgents = useMemo(() => {
    const myId = String(user?._id || user?.id || user?.userId || '');
    const myPresenceStatus = agentPresence?.status || 'online';
    const myIsAvailable = myPresenceStatus === 'online';

    if (!myId) return agents;

    /** Purpose: Check whether the current user already appears in the agent list. Why: Reconciliation avoids creating a duplicate self entry. */
    const found = agents.some((a) => String(a.userId || a._id) === myId);
    if (found) {
      return agents.map((a) => {
        if (String(a.userId || a._id) === myId) {
          return {
            ...a,
            status: myPresenceStatus,
            isAvailable: myIsAvailable,
            available: myIsAvailable,
          };
        }
        return a;
      });
    }

    const isAgentOrAdmin = ['AGENT', 'ADMIN'].includes(String(user?.role || '').toUpperCase());
    if (isAgentOrAdmin) {
      return [
        ...agents,
        {
          _id: myId,
          userId: myId,
          name: user?.name || user?.email || 'Agent',
          email: user?.email || '',
          status: myPresenceStatus,
          isAvailable: myIsAvailable,
          available: myIsAvailable,
          activeChats: 0,
          maxLimit: 3,
        },
      ];
    }
    return agents;
  }, [agents, user, agentPresence?.status]);

  /** Purpose: Resolve the agent assigned to the selected conversation. Why: Details UI must show the assignee using current agent records. */
  const selectedAgent = useMemo(
    () => reconciledAgents.find((agent) => String(agent.userId || agent._id) === String(selectedConversation?.assignedTo?._id || selectedConversation?.assignedTo)) || null,
    [reconciledAgents, selectedConversation]
  );

  /** Purpose: Sum unread counts across the loaded conversations. Why: The inbox badge should match currently loaded unread work. */
  const totalUnread = useMemo(
    () => conversations.reduce((sum, conversation) => sum + (conversation.unreadCount || 0), 0),
    [conversations]
  );

  /** Purpose: Shape contacts into the list's display and navigation fields. Why: Contact cards need a stable format across contact and conversation data. */
  const contactRows = useMemo(() => {
    if (contacts.length) return contacts.map((contact) => ({ ...contact, displayName: contact.name || contact.mobile || contact.email || contact.channelUserId, userId: contact.customerId || contact.channelUserId, lastMessage: `${contact.conversationCount || 0} conversations · ${contact.email || contact.mobile || contact.channel}`, lastMessageAt: contact.lastSeen }));
    const grouped = new Map();

    conversations.forEach((conversation) => {
      const contactKey = conversation.channelUserId || conversation.userId || conversation.displayName || conversation._id;
      const existing = grouped.get(contactKey);
      const nextCount = (existing?.conversationCount || 0) + 1;
      const latestConversation = !existing
        || new Date(conversation.lastMessageAt || conversation.updatedAt || conversation.createdAt || 0)
          >= new Date(existing.latestConversation?.lastMessageAt || existing.latestConversation?.updatedAt || existing.latestConversation?.createdAt || 0)
        ? conversation
        : existing.latestConversation;

      grouped.set(contactKey, {
        _id: latestConversation?._id || conversation._id,
        contactKey,
        displayName: conversation.displayName || conversation.userId || conversation.channelUserId || 'Visitor',
        userId: conversation.userId || conversation.channelUserId || '',
        channel: latestConversation?.channel || conversation.channel || 'web',
        lastMessage: latestConversation?.lastMessage
          ? `${nextCount} conversation${nextCount > 1 ? 's' : ''} • ${latestConversation.lastMessage}`
          : `${nextCount} conversation${nextCount > 1 ? 's' : ''}`,
        lastMessageAt: latestConversation?.lastMessageAt || latestConversation?.updatedAt || latestConversation?.createdAt || conversation.lastMessageAt,
        unreadCount: (existing?.unreadCount || 0) + (conversation.unreadCount || 0),
        status: latestConversation?.status || conversation.status || 'open',
        latestConversation: latestConversation || conversation,
        conversationCount: nextCount,
      });
    });

    return Array.from(grouped.values()).sort(
      (a, b) => new Date(b.lastMessageAt || b.updatedAt || 0) - new Date(a.lastMessageAt || a.updatedAt || 0)
    );
  }, [contacts, conversations]);

  /** Purpose: Aggregate conversation counts by status. Why: Inbox summary cards should derive from the same visible conversation data. */
  const dashboardStats = useMemo(() => ({
    total: conversations.length,
    open: conversations.filter((conversation) => conversation.status === 'open').length,
    assigned: conversations.filter((conversation) => conversation.status === 'assigned' || conversation.status === 'pending').length,
    resolved: conversations.filter((conversation) => conversation.status === 'resolved').length,
    unread: totalUnread,
    contacts: contactRows.length,
    channels: conversations.reduce((acc, conversation) => {
      const key = conversation.channel || 'web';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {}),
  }), [contactRows.length, conversations, totalUnread]);
  /** Purpose: Count conversations for each operational status. Why: The chart should expose queue distribution in a predictable status order. */
  const operationalChart = useMemo(() => ['queued','open','assigned','active','resolved','missed'].map((status) => ({ status, conversations: conversations.filter((item) => item.status === status).length })), [conversations]);

  /** Purpose: Summarize online, available, busy, and offline agents. Why: Assignment readiness depends on availability, not only online presence. */
  const agentStats = useMemo(() => {
    /** Purpose: Select agents with online presence. Why: Only online operators can be considered for immediate work. */
    const onlineList = reconciledAgents.filter((agent) => agent.status === 'online');
    /** Purpose: Select online agents who are available for assignment. Why: Busy agents must not inflate assignable capacity. */
    const availableList = onlineList.filter((agent) => agent.isAvailable !== false);
    return {
      online: onlineList.length,
      available: availableList.length,
      busy: reconciledAgents.filter((agent) => agent.status === 'busy').length,
      offline: reconciledAgents.filter((agent) => agent.status === 'offline').length,
      names: onlineList.map((agent) => agent.name || agent.email).filter(Boolean),
    };
  }, [reconciledAgents]);

  /** Purpose: Apply the active inbox category and ticket filters. Why: Chats, bot conversations, tickets, and missed work need distinct queues. */
  const visibleConversations = useMemo(() => {
    if (activeSection === 'bot_chat') {
      return conversations.filter((conversation) => isBotOnlyConversation(conversation));
    }

    if (activeSection === 'chats') {
      /** Purpose: Keep non-ticket conversations while retaining the active item. Why: Ticket filtering should not hide the conversation currently open in the thread. */
      const nonTicketConversations = conversations.filter((conversation) => {
        if (normalizeId(conversation._id) === normalizeId(currentConversationIdRef.current)) return true;
        return !isTicketConversation(conversation);
      });
      if (chatView === 'bot') return nonTicketConversations.filter(isBotOnlyConversation);
      return nonTicketConversations;
    }

    if (activeSection === 'tickets') {
      return conversations.filter((conversation) => matchesTicketView(conversation, filters.status));
    }
    if (activeSection === 'missed-chats') {
      return conversations.filter((conversation) => matchesTicketView(conversation, 'missed'));
    }

    return conversations;
  }, [activeSection, chatView, conversations, selectedConversation?._id, filters.status]);

  /** Purpose: Filter shaped contacts using the search query. Why: Contact lookup should respond to the same search control as the inbox. */
  const visibleContacts = useMemo(() => {
    const query = String(filters.search || '').trim().toLowerCase();
    return contactRows.filter((item) => {
      if (!query) return true;
      return [item.displayName, item.userId, item.lastMessage, item.channel]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [contactRows, filters.search]);

  /** Purpose: Persist contact edits in the active bot/environment and refresh contacts. Why: Customer identity changes must remain scoped and immediately visible. */
  const saveContact = useCallback(async (contactId, patch) => {
    await axios.patch(`/api/v1/agent-desk/contacts/${contactId}`, { ...patch, botId: selectedBot, env: selectedEnv });
    await fetchContacts();
  }, [fetchContacts, selectedBot, selectedEnv]);

  useEffect(() => {
    if (activeSection === 'dashboard' || activeSection === 'contacts') {
      return;
    }

    if (!visibleConversations.length) {
      if (!currentConversationIdRef.current) {
        setSelectedConversation(null);
        setMessages([]);
      }
      return;
    }

    /** Purpose: Check whether the currently open conversation remains in the visible list. Why: Filtering and refresh should preserve a valid selection when possible. */
    const currentVisible = visibleConversations.find(
      (conversation) => normalizeId(conversation._id) === normalizeId(currentConversationIdRef.current),
    );
    if (!currentVisible) {
      if (!currentConversationIdRef.current && visibleConversations[0]) {
        setSelectedConversation(visibleConversations[0]);
        currentConversationIdRef.current = visibleConversations[0]?._id || null;
      }
    }
  }, [activeSection, visibleConversations]);

  const sectionNavigation = (
    <div className="flex min-w-0 items-center justify-center gap-1 overflow-x-auto">
      {INBOX_SECTIONS.map((sectionItem) => {
        const active = activeSection === sectionItem.key;
        const count = sectionItem.key === 'dashboard'
          ? dashboardStats.total
          : sectionItem.key === 'contacts'
            ? dashboardStats.contacts
              : sectionItem.key === 'chats'
              ? conversations.filter((conversation) => !isTicketConversation(conversation)).length
              : sectionItem.key === 'missed-chats'
                ? missedCount
              : sectionItem.key === 'tickets'
                ? conversations.filter(isUnresolvedTicket).length
                : dashboardStats.total;

        return (
          <button
            key={sectionItem.key}
            type="button"
            onClick={() => {
              setActiveSection(sectionItem.key);
              setFilters((current) => ({ ...current, status: sectionItem.key === 'missed-chats' ? 'missed' : 'all' }));
              currentConversationIdRef.current = null;
              setSelectedConversation(null);
              setMessages([]);
              setChatView('all');
              setError('');
              navigate(`/inbox/${sectionItem.key}`);
            }}
            className={[
              'inline-flex shrink-0 items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-xs font-medium transition',
              active
                ? 'border-cyan-400/30 bg-cyan-500/10 text-cyan-700 shadow-sm dark:text-cyan-200'
                : 'border-slate-200 bg-white text-slate-600 hover:border-cyan-300 hover:bg-cyan-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10',
            ].join(' ')}
          >
            {sectionItem.icon}
            {sectionItem.label}
            <span className="rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] font-semibold text-current dark:bg-white/10">
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );

  const inboxHeaderCard = (
    <div className="flex shrink-0 items-center gap-1.5 min-w-0 overflow-x-auto py-0.5 no-scrollbar">
      <h1 className="text-sm font-bold tracking-tight text-slate-900 shrink-0 dark:text-white hidden sm:block">Inbox</h1>

      <div className="flex items-center gap-1 shrink-0">
        <Chip
          label={`${totalUnread} unread`}
          size="small"
          className="!rounded-full !border !border-cyan-400/20 !bg-cyan-500/10 !text-[11px] !font-medium !text-cyan-700 dark:!text-cyan-200"
        />
        <Chip
          size="small"
          label={`${agentStats.available} available agent${agentStats.available === 1 ? '' : 's'}`}
          title={agentStats.names.length ? `Online agents (${agentStats.online}): ${agentStats.names.join(', ')}` : 'No online agents'}
          className="!rounded-full !border !border-emerald-400/20 !bg-emerald-500/10 !text-[11px] !font-medium !text-emerald-700 dark:!text-emerald-200"
        />
        {agentStats.busy > 0 ? (
          <Chip
            size="small"
            label={`${agentStats.busy} busy`}
            className="!rounded-full !border !border-amber-400/20 !bg-amber-500/10 !text-[11px] !font-medium !text-amber-700 dark:!text-amber-200"
          />
        ) : null}
        {socketReady ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="hidden md:inline">Live updates active</span>
            <span className="md:hidden">Live</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            <span className="hidden md:inline">Polling sync active</span>
            <span className="md:hidden">Polling</span>
          </span>
        )}
      </div>

      {String(user?.role || '').toUpperCase() === 'AGENT' || String(user?.role || '').toUpperCase() === 'ADMIN' ? (
        <div className="flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200/90 bg-white/95 px-2 py-0.5 shadow-2xs dark:border-white/10 dark:bg-slate-900/90">
          <span className={`h-2 w-2 rounded-full ${agentPresence?.status === 'online' ? 'bg-emerald-500' : 'bg-amber-400'}`} />
          <select
            value={agentPresence?.status || 'online'}
            onChange={(event) => {
              const status = event.target.value;
              if (status !== 'custom') setCustomPresenceText('');
              void changeMyPresence(status, status === 'custom' ? customPresenceText : '');
            }}
            className="bg-transparent text-xs font-semibold text-slate-700 outline-none dark:text-slate-200 cursor-pointer"
          >
            <option value="online">Available</option>
            <option value="busy">Busy</option>
            <option value="away">Away</option>
            <option value="break">On break</option>
            <option value="custom">Custom</option>
            <option value="offline">Offline</option>
          </select>
          {agentPresence?.status === 'custom' ? (
            <input
              autoFocus
              value={customPresenceText}
              onChange={(event) => setCustomPresenceText(event.target.value)}
              onBlur={() => void changeMyPresence('custom', customPresenceText)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
              placeholder="Your status"
              maxLength={80}
              className="w-24 border-l border-slate-200 bg-transparent pl-1.5 text-xs outline-none dark:border-white/10"
            />
          ) : null}
        </div>
      ) : null}

      <div className="flex shrink-0 items-center gap-1.5 pl-0.5">
        <label className="flex items-center gap-1 text-[11px] font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={notificationPrefs.browser}
            onChange={(e) => updateNotificationPrefs({ browser: e.target.checked })}
            className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
          />
          <span className="hidden lg:inline">Browser alerts</span>
          <span className="lg:hidden">Alerts</span>
        </label>
        <div className="flex items-center gap-1">
          <label className="flex items-center gap-1 text-[11px] font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={notificationPrefs.sound}
              onChange={(e) => updateNotificationPrefs({ sound: e.target.checked })}
              className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
            />
            <span>Sound</span>
          </label>
          <button
            type="button"
            onClick={async () => {
              const played = await playTicketChime();
              setSoundTested(played ? 'ready' : 'blocked');
              setTimeout(() => setSoundTested(''), 3000);
            }}
            title="Click to test sound chime and ensure browser audio is enabled"
            className="inline-flex items-center gap-0.5 rounded-lg border border-slate-200/90 bg-slate-50 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 transition-colors cursor-pointer"
          >
            {soundTested === 'ready' ? '🔔 Sound on' : soundTested === 'blocked' ? '🔕 Sound blocked' : '🔔 Test sound'}
          </button>
        </div>
      </div>
    </div>
  );

  if (!selectedBot) {
    return (
      <div className="rounded-[28px] border border-slate-200/80 bg-white/90 p-6 shadow-[0_12px_40px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-950/80">
        <div className="text-lg font-semibold text-slate-900 dark:text-white">Inbox</div>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Select a bot to view unified conversations from Web, WhatsApp, and Voice.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex flex-1 min-h-0 w-full h-full flex-col gap-2 overflow-hidden"
    >
      {headerActionsTarget ? (
        createPortal(inboxHeaderCard, headerActionsTarget)
      ) : (
        <div className="flex shrink-0 flex-wrap items-center gap-2.5 rounded-2xl border border-slate-200/80 bg-white/90 px-3.5 py-1.5 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/80">
          {inboxHeaderCard}
        </div>
      )}

      {headerNavigationTarget ? createPortal(sectionNavigation, headerNavigationTarget) : null}
      <div className="flex shrink-0 rounded-2xl border border-slate-200/80 bg-white/90 p-1.5 shadow-sm xl:hidden dark:border-white/10 dark:bg-slate-950/80">
        {sectionNavigation}
      </div>

      {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
      {notifications[0] ? <Alert severity="info" onClose={() => setNotifications((current) => current.slice(1))}>{notifications[0].title}: {notifications[0].body}</Alert> : null}

      {activeSection !== 'dashboard' ? (
        <div className="flex shrink-0 items-center justify-between gap-2 rounded-xl border border-slate-200/80 bg-white/90 px-2.5 py-1.5 shadow-2xs backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/80">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange({ priority: e.target.value })}
              className="h-7 rounded-lg border border-slate-200/90 bg-white px-2 text-xs font-medium text-slate-700 outline-none transition hover:border-slate-300 focus:border-cyan-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">All priorities</option>
              {['urgent', 'high', 'normal', 'low'].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>

            <select
              value={filters.slaStatus}
              onChange={(e) => handleFilterChange({ slaStatus: e.target.value })}
              className="h-7 rounded-lg border border-slate-200/90 bg-white px-2 text-xs font-medium text-slate-700 outline-none transition hover:border-slate-300 focus:border-cyan-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            >
              <option value="">All SLA states</option>
              {['healthy', 'warning', 'breached'].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>

            <input
              value={filters.tag}
              onChange={(e) => handleFilterChange({ tag: e.target.value })}
              placeholder="Tag..."
              className="h-7 w-24 sm:w-28 rounded-lg border border-slate-200/90 bg-white px-2 text-xs text-slate-700 placeholder:text-slate-400 outline-none transition hover:border-slate-300 focus:border-cyan-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            />

            <div className="flex items-center gap-1">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange({ dateFrom: e.target.value })}
                className="h-7 rounded-lg border border-slate-200/90 bg-white px-2 text-xs text-slate-700 outline-none transition hover:border-slate-300 focus:border-cyan-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
              />
              <span className="text-[10px] text-slate-400">to</span>
              <input
                type="date"
                value={filters.dateTo ? filters.dateTo.split('T')[0] : ''}
                onChange={(e) => handleFilterChange({ dateTo: e.target.value ? `${e.target.value}T23:59:59.999Z` : '' })}
                className="h-7 rounded-lg border border-slate-200/90 bg-white px-2 text-xs text-slate-700 outline-none transition hover:border-slate-300 focus:border-cyan-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setFilters(defaultFilters)}
            className="h-7 shrink-0 rounded-lg border border-slate-200/90 bg-slate-50 px-2.5 text-xs font-semibold text-slate-600 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-rose-950/20 cursor-pointer"
          >
            Clear
          </button>
        </div>
      ) : null}

      {activeSection === 'dashboard' ? (
        <div className="grid min-h-0 flex-1 gap-3 overflow-y-auto xl:grid-cols-[minmax(0,1fr)_380px]">
          <section className="rounded-[28px] border border-slate-200/80 bg-white/90 p-5 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/80">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {[
                { label: 'Open conversations', value: dashboardStats.open, tone: 'text-emerald-600 dark:text-emerald-300' },
                { label: 'Assigned follow-ups', value: dashboardStats.assigned, tone: 'text-amber-600 dark:text-amber-300' },
                { label: 'Resolved', value: dashboardStats.resolved, tone: 'text-slate-700 dark:text-slate-200' },
                { label: 'Unread messages', value: dashboardStats.unread, tone: 'text-cyan-600 dark:text-cyan-300' },
                { label: 'Unified contacts', value: dashboardStats.contacts, tone: 'text-violet-600 dark:text-violet-300' },
                { label: 'Total conversations', value: dashboardStats.total, tone: 'text-slate-900 dark:text-white' },
              ].map((card) => (
                <div key={card.label} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
                  <div className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-500">{card.label}</div>
                  <div className={`mt-2 text-3xl font-semibold ${card.tone}`}>{card.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
              <div className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-500">Channel mix</div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {Object.keys(CHANNEL_META).map((channel) => (
                  <div key={channel} className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900/80">
                    <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${channelTone[channel] || 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-200'}`}>
                      {CHANNEL_META[channel].icon}
                      {CHANNEL_META[channel].label}
                    </div>
                    <div className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
                      {dashboardStats.channels[channel] || 0}
                    </div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">Conversations</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-5 h-64 rounded-3xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900/80"><div className="mb-3 flex items-center justify-between gap-3"><div className="text-xs uppercase tracking-[0.24em] text-slate-500">Conversation status</div><select value={analyticsRange} onChange={(event) => setAnalyticsRange(event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs dark:border-white/10 dark:bg-slate-900"><option value="1">24 hours</option><option value="7">7 days</option><option value="30">30 days</option></select></div><ResponsiveContainer width="100%" height="85%"><BarChart data={analytics?.byStatus || operationalChart}><XAxis dataKey="status" fontSize={11}/><YAxis allowDecimals={false} fontSize={11}/><Tooltip/><Bar dataKey="conversations" fill="#06b6d4" radius={[8,8,0,0]}/></BarChart></ResponsiveContainer></div>
          </section>

          <section className="rounded-[28px] border border-slate-200/80 bg-white/90 p-5 shadow-[0_12px_40px_rgba(15,23,42,0.06)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/80">
            <div className="text-xs uppercase tracking-[0.28em] text-slate-500 dark:text-slate-500">Recent activity</div>
            <div className="mt-4 space-y-3">
              {conversations.slice(0, 6).map((conversation) => (
                <div
                  key={conversation._id}
                  onClick={() => {
                    handleSelectConversation(conversation);
                    const targetSection = isTicketConversation(conversation) ? 'tickets' : 'chats';
                    setActiveSection(targetSection);
                    navigate(`/inbox/${targetSection}?conversationId=${conversation._id}`);
                  }}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/5 cursor-pointer hover:bg-slate-100 dark:hover:bg-white/10 transition"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                        {conversation.displayName || conversation.userId || conversation.channelUserId || 'Visitor'}
                      </div>
                      <div className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                        {conversation.lastMessage || 'No messages yet'}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${channelTone[conversation.channel] || channelTone.web}`}>
                      {CHANNEL_META[conversation.channel || 'web']?.icon}
                      {conversation.channel || 'web'}
                    </span>
                  </div>
                </div>
              ))}
              {!conversations.length ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                  No conversations yet.
                </div>
              ) : null}
            </div>
          </section>
        </div>
      ) : (
        <div className={[
          "grid min-h-0 flex-1 gap-2.5 overflow-hidden",
          showDetails
            ? "xl:grid-cols-[280px_minmax(0,1fr)_220px] 2xl:grid-cols-[300px_minmax(0,1fr)_230px]"
            : "xl:grid-cols-[290px_minmax(0,1fr)] 2xl:grid-cols-[320px_minmax(0,1fr)]"
        ].join(' ')}>
          <InboxConversationList
            conversations={activeSection === 'contacts' ? visibleContacts : visibleConversations}
            filters={activeSection === 'contacts'
              ? { ...filters, status: 'all', channel: 'all', assignedTo: 'all', mine: false }
              : filters}
            onFilterChange={handleFilterChange}
            activeConversationId={selectedConversationId}
            loading={loadingConversations}
            onSelectConversation={handleSelectConversation}
            pagination={pagination}
            onLoadMore={activeSection === 'contacts' ? undefined : handleLoadMore}
            conversationKind={activeSection === 'chats'
              ? (conversation) => (isBotOnlyConversation(conversation) ? 'Bot conversation' : 'Agent chat')
              : undefined}
            statusMode={activeSection === 'chats' ? 'bot' : activeSection === 'tickets' ? 'ticket' : ['contacts', 'missed-chats'].includes(activeSection) ? 'none' : 'conversation'}
            chatView={activeSection === 'chats' ? chatView : undefined}
            onChatViewChange={setChatView}
            botChatCount={conversations.filter((item) => isBotOnlyConversation(item) && !isTicketConversation(item)).length}
          />

          {activeSection === 'contacts' ? (
            <section className="rounded-[28px] border border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-slate-950/80">
              <h2 className="text-xl font-semibold">Contact profile</h2>
              {selectedConversation ? (
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {['name','mobile','email','language','customerId'].map((field) => (
                    <label key={field} className="text-xs uppercase tracking-wide text-slate-500">
                      {field}
                      <input
                        defaultValue={selectedConversation[field] || ''}
                        onBlur={(e) => saveContact(selectedConversation._id, { [field]: e.target.value })}
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm normal-case dark:border-white/10 dark:bg-slate-900"
                      />
                    </label>
                  ))}
                  <div className="sm:col-span-2 text-sm text-slate-500">
                    First seen: {selectedConversation.firstSeen ? new Date(selectedConversation.firstSeen).toLocaleString() : '-'} · Last seen: {selectedConversation.lastSeen ? new Date(selectedConversation.lastSeen).toLocaleString() : '-'}
                  </div>
                </div>
              ) : (
                <div className="mt-6 text-sm text-slate-500">Select a contact.</div>
              )}
            </section>
          ) : (
            <InboxThread
              conversation={selectedConversation}
              messages={messages}
              loading={loadingMessages}
              sending={sending}
              typing={remoteTyping}
              typingText={remoteTypingText}
              onSendMessage={sendMessage}
              onSendAttachment={sendAttachment}
              onOpenAttachment={openAttachment}
              capabilities={capabilities}
              onTyping={emitTyping}
              onAccept={acceptConversation}
              onReject={rejectConversation}
              onResolve={() => setStatus('resolved')}
              resolving={resolvingConversationId === normalizeId(selectedConversationId)}
              onMarkRead={() => selectedConversationId && axios.patch(`/api/v1/inbox/conversations/${selectedConversationId}/read`)}
              cannedResponses={cannedResponses}
              showDetails={showDetails}
              onToggleDetails={() => setShowDetails((prev) => !prev)}
              isBotConversation={activeSection === 'chats' && Boolean(selectedConversation) && isBotOnlyConversation(selectedConversation)}
            />
          )}

          {activeSection !== 'contacts' && showDetails ? (
            <div className="hidden min-h-0 xl:block h-full">
              <InboxDetailsPanel
                conversation={selectedConversation}
                agents={reconciledAgents}
                selectedAgent={selectedAgent}
                onAssign={assignConversation}
                onStatusChange={setStatus}
                isBotConversation={activeSection === 'chats' && Boolean(selectedConversation) && isBotOnlyConversation(selectedConversation)}
                onAddNote={addNote}
                onAddTag={addTag}
                onRemoveTag={removeTag}
              />
            </div>
          ) : activeSection === 'contacts' ? (
            <section className="rounded-[28px] border border-slate-200 bg-white/90 p-5 text-sm text-slate-500 dark:border-white/10 dark:bg-slate-950/80">
              Contact tags and custom values are stored against this workspace and remain available across conversations.
            </section>
          ) : null}
        </div>
      )}
    </motion.div>
  );
}
